import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, ChevronDown, Tag, SlidersHorizontal } from "lucide-react";
import WinbackOffers from "@/components/admin_dashboard/WinbackOffers";
import WinbackTemplates from "@/components/admin_dashboard/WinbackTemplates";

// The single "Advanced settings" page for the business's AI. The AI has no
// name or persona of its own (see supabase/functions/_shared/ai/agentIdentity.ts),
// so there is no name to edit here and ai_agent_configs.agent_name is unused.

export const StorefrontAI = () => {
  const { businessId } = useAuth();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Personality config state
  const [isActive, setIsActive] = useState(true);
  const [customInstructions, setCustomInstructions] = useState("");
  const [showDefaultVoice, setShowDefaultVoice] = useState(false);

  const fetchData = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      const { data: configData } = await supabase
        .from("ai_agent_configs")
        .select("*")
        .eq("business_id", businessId)
        .maybeSingle();

      if (configData) {
        setIsActive(configData.is_active ?? true);
        // Prefer the new additive field; fall back to a legacy full-override
        // value so businesses that authored one before the additive model
        // still see their text (it keeps working as a full override server-side).
        setCustomInstructions(configData.custom_instructions || configData.system_prompt_override || "");
      }
    } catch (e) {
      console.error("Error loading AI settings:", e);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!businessId) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("ai_agent_configs")
        .upsert({
          business_id: businessId,
          is_active: isActive,
          custom_instructions: customInstructions,
          // Clear any legacy full-override so the additive model takes over:
          // base persona + these custom instructions layered on top.
          system_prompt_override: null,
          updated_at: new Date().toISOString(),
        }, { onConflict: "business_id" });

      if (error) throw error;

      toast({
        title: "AI Settings Saved",
        description: "Your AI concierge configuration has been updated.",
      });
    } catch (e: any) {
      console.error("Error saving AI settings:", e);
      toast({
        title: "Error",
        description: e.message || "Failed to save AI settings.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 pb-16">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <SlidersHorizontal className="h-5 w-5 text-primary" />
          </span>
          <div className="space-y-1">
            <h2 className="text-xl font-heading font-semibold text-foreground">Advanced settings</h2>
            <p className="text-sm text-muted-foreground">
              Shape how your AI talks to customers.
            </p>
          </div>
        </div>
        <div className="p-6 border rounded-xl bg-card shadow-sm space-y-4">
          <Skeleton className="h-6 w-1/3 mb-2" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-2 w-full" />
        </div>
      </div>
    );
  }

  const triggerClass =
    "bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2";

  return (
    <div className="space-y-6 pb-16 animate-in fade-in duration-500">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <SlidersHorizontal className="h-5 w-5 text-primary" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-xl font-heading font-semibold text-foreground">Advanced settings</h2>
          <p className="text-sm text-muted-foreground">
            Shape how your AI talks to customers and what it can do on its own.
          </p>
        </div>
      </div>

      <Tabs defaultValue="personality" className="w-full">
        <TabsList className="bg-transparent border-b border-border p-0 w-full justify-start rounded-none h-auto gap-6 mb-6">
          <TabsTrigger value="personality" className={triggerClass}>
            <Sparkles className="h-4 w-4" />
            Personality
          </TabsTrigger>
          <TabsTrigger value="offers" className={triggerClass}>
            <Tag className="h-4 w-4" />
            Offers
          </TabsTrigger>
        </TabsList>

        {/* ── Personality tab ───────────────────────────────────────────── */}
        <TabsContent value="personality" className="mt-0">
          <div className="p-6 border rounded-xl bg-card shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div className="space-y-1">
                <p className="text-sm font-bold uppercase tracking-wider text-foreground">Personality & settings</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Customize how your AI interacts with customers.
                </p>
              </div>
              <div className="flex items-center gap-2 border rounded-full px-3 py-1.5 shadow-sm bg-background self-start">
                <Label htmlFor="ai-toggle" className={`text-sm font-medium cursor-pointer ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                  {isActive ? "Active" : "Paused"}
                </Label>
                <Switch
                  id="ai-toggle"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </div>

            <div className="space-y-6">
              {/* Read-only summary of the baked-in voice so owners can see what
                  their agent already does before adding their own rules. */}
              <div className="rounded-lg border border-border bg-muted/40">
                <button
                  type="button"
                  onClick={() => setShowDefaultVoice((v) => !v)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Sparkles className="h-4 w-4 text-primary" />
                    How your AI sounds by default
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${showDefaultVoice ? "rotate-180" : ""}`}
                  />
                </button>
                {showDefaultVoice && (
                  <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground space-y-2 leading-relaxed">
                    <p>
                      Your AI starts with a warm, human voice — no setup needed. It already:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Texts like a friendly receptionist on WhatsApp — short, relaxed, never robotic, and barely uses emojis.</li>
                      <li>Sells by genuinely caring: leads with what the customer gets, reassures first-timers, and nudges gently without ever being pushy.</li>
                      <li>Replies in the customer's own language (English, Malay, Chinese, Singlish, and more).</li>
                      <li>Sticks to your real services, prices, and policies — it never makes things up, and hands off to your team when it's truly stuck.</li>
                    </ul>
                    <p>
                      Your custom instructions below stack <span className="font-medium text-foreground">on top</span> of this — they add to the voice, they don't replace it.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="custom-instructions">Custom instructions <span className="font-normal text-muted-foreground">(optional)</span></Label>
                </div>
                <Textarea
                  id="custom-instructions"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="E.g. Always mention our 10% first-timer discount. We're closed on public holidays. Recommend the Signature Facial to anyone asking about skincare."
                  className="min-h-[200px] resize-y"
                />
                <p className="text-sm text-muted-foreground leading-snug">
                  Add your own rules, facts, or offers. These layer on top of the default voice above — no need to re-describe the tone. Leave blank to use the default as-is.
                </p>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="min-w-[120px] shadow-sm hover:shadow transition-all"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Offers tab ────────────────────────────────────────────────── */}
        <TabsContent value="offers" className="mt-0 space-y-6">
          <div className="p-6 border rounded-xl bg-card shadow-sm">
            <WinbackOffers />
          </div>
          <div className="p-6 border rounded-xl bg-card shadow-sm">
            <WinbackTemplates />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StorefrontAI;
